import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, registerQuery } from 'data-fetching-client';
import http from 'hub-http/clients/apiClient';
import usePrevious from 'react-utils/hooks/usePrevious';
import isEqual from 'hs-lodash/isEqual';
import { ALL_USERS, HUBSPOT_ACTOR_TYPE, USER_ACTOR_TYPE } from '../constants/filterOptions';
import { parseActivityResult } from '../utils/activityResult';
import { OBJECT_TYPE } from 'activity-feed-lib/constants/ObjectTypes';
export const POLLING_INTERVAL = 10000;
const QUERY_ARGS = ['objectId', 'objectTypeId', 'startTimestamp', 'endTimestamp', 'activityTypes', 'userId', 'limit', 'after', 'searchQuery'];
const activityFeedFetcher = ({
  objectId,
  objectTypeId,
  startTimestamp,
  endTimestamp,
  activityTypes,
  userId,
  after,
  limit = 10,
  searchQuery
}) => {
  const activityActorId = userId === ALL_USERS || userId === HUBSPOT_ACTOR_TYPE ? undefined : userId;
  const activityActorType = getActorType(userId);
  const feedQuery = {
    limit,
    startTimestamp,
    endTimestamp,
    activityTypes,
    activityActorId,
    activityActorType,
    after,
    searchQuery
  };

  /**
   * Campaign feed: You see everything happening to a campaign and its associated assets.
   * This is exactly the same as taking the portal view, and filtering by a specific campaign.
   *
   * Slack: https://hubspot.slack.com/archives/C01LHLYF7K4/p1691073243156919
   */
  if (objectTypeId === OBJECT_TYPE.CAMPAIGN) {
    feedQuery.campaignObjectId = objectId;
  } else {
    feedQuery.objectId = objectId;
    feedQuery.objectTypeId = objectTypeId;
  }
  return http.get('marketing-activity-feed/v1/feed', {
    query: feedQuery
  });
};
const pollingFetcher = ({
  objectId,
  objectTypeId,
  startTimestamp,
  endTimestamp,
  activityTypes,
  userId,
  after,
  searchQuery
}) => {
  const activityActorId = userId === ALL_USERS || userId === HUBSPOT_ACTOR_TYPE ? undefined : userId;
  const activityActorType = getActorType(userId);
  const feedQuery = {
    startTimestamp,
    endTimestamp,
    activityTypes,
    activityActorId,
    activityActorType,
    after,
    searchQuery
  };

  /**
   * Campaign feed: You see everything happening to a campaign and its associated assets.
   * This is exactly the same as taking the portal view, and filtering by a specific campaign.
   *
   * Slack: https://hubspot.slack.com/archives/C01LHLYF7K4/p1691073243156919
   */
  if (objectTypeId === OBJECT_TYPE.CAMPAIGN) {
    feedQuery.campaignObjectId = objectId;
  } else {
    feedQuery.objectId = objectId;
    feedQuery.objectTypeId = objectTypeId;
  }
  return http.get('marketing-activity-feed/v1/feed/latest', {
    query: feedQuery
  });
};
function getActorType(userId) {
  if (!userId || userId === ALL_USERS) {
    return undefined;
  }
  if (userId === HUBSPOT_ACTOR_TYPE) {
    return HUBSPOT_ACTOR_TYPE;
  }
  return USER_ACTOR_TYPE;
}
export const GET_ACTIVITY_FEED_FIELD_NAME = 'activityFeed';
const GET_ACTIVITY_FEED = registerQuery({
  fieldName: GET_ACTIVITY_FEED_FIELD_NAME,
  args: QUERY_ARGS,
  fetcher: activityFeedFetcher
});
export const POLL_ACTIVITY_FEED_FIELD_NAME = 'pollActivityFeed';
const POLL_ACTIVITY_FEED = registerQuery({
  fieldName: POLL_ACTIVITY_FEED_FIELD_NAME,
  args: QUERY_ARGS,
  fetcher: pollingFetcher
});
function parseResponse({
  data,
  error,
  loading,
  isFirstFetch
}) {
  if (error || isFirstFetch && loading) {
    return undefined;
  }
  if (!(data && GET_ACTIVITY_FEED_FIELD_NAME in data)) {
    return [];
  }
  return data[GET_ACTIVITY_FEED_FIELD_NAME].results.map(parseActivityResult);
}
export default function useFetchActivityFeed({
  objectId,
  objectTypeId,
  startTimestamp,
  endTimestamp,
  activityTypes,
  userId,
  searchQuery
}) {
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const [isRefetchLoading, setIsRefetchLoading] = useState(false);
  const {
    data,
    error,
    loading,
    fetchMore
  } = useQuery(GET_ACTIVITY_FEED, {
    variables: {
      objectId,
      objectTypeId,
      startTimestamp,
      endTimestamp,
      activityTypes,
      userId,
      searchQuery,
      after: undefined,
      limit: 10
    }
  });
  const currentFilters = useMemo(() => ({
    objectId,
    objectTypeId,
    startTimestamp,
    endTimestamp,
    activityTypes,
    userId,
    searchQuery
  }), [activityTypes, endTimestamp, objectId, objectTypeId, searchQuery, startTimestamp, userId]);
  const previousFilters = usePrevious(currentFilters);

  // Changes isFirstFetch value when first request has loaded
  useEffect(() => {
    if (!isEqual(previousFilters, currentFilters)) {
      setIsFirstFetch(true);
    }
    if (isFirstFetch && !loading) {
      setIsFirstFetch(false);
    }
  }, [currentFilters, isFirstFetch, loading, previousFilters]);
  const activityFeed = parseResponse({
    data,
    error,
    loading,
    isFirstFetch
  });
  const {
    hasMore,
    offset
  } = data && GET_ACTIVITY_FEED_FIELD_NAME in data ? data[GET_ACTIVITY_FEED_FIELD_NAME] : {
    hasMore: false,
    offset: null
  };
  const fetchMoreFeed = useCallback(() => {
    if (hasMore && offset && !loading) {
      setIsRefetchLoading(true);
      fetchMore({
        variables: {
          objectId,
          objectTypeId,
          after: offset,
          limit: 10,
          startTimestamp,
          endTimestamp,
          activityTypes,
          userId,
          searchQuery
        }
      }).then(() => {
        setIsRefetchLoading(false);
      }).catch(() => {
        setIsRefetchLoading(false);
      });
    }
  }, [activityTypes, endTimestamp, fetchMore, hasMore, loading, objectId, objectTypeId, offset, searchQuery, startTimestamp, userId]);
  return {
    activityFeed,
    hasMore,
    isFirstFetch,
    fetchMoreFeed,
    fetchMore,
    error,
    loading: loading || isRefetchLoading
  };
}
export function usePollActivityFeed({
  objectId,
  objectTypeId,
  startTimestamp,
  endTimestamp,
  activityTypes,
  userId,
  searchQuery,
  skip,
  mostRecentEventId
}) {
  const [hasNewActivities, setHasNewActivities] = useState(false);
  const {
    data,
    loading,
    error
  } = useQuery(POLL_ACTIVITY_FEED, {
    variables: {
      objectId,
      objectTypeId,
      startTimestamp,
      endTimestamp,
      activityTypes,
      userId,
      searchQuery,
      after: undefined
    },
    skip,
    pollInterval: hasNewActivities ? 0 : POLLING_INTERVAL
  });
  useEffect(() => {
    setHasNewActivities(Boolean(!loading && !error && data && mostRecentEventId && data[POLL_ACTIVITY_FEED_FIELD_NAME] && data[POLL_ACTIVITY_FEED_FIELD_NAME].id !== mostRecentEventId));
  }, [data, error, loading, mostRecentEventId]);
  return {
    hasNewActivities,
    setHasNewActivities
  };
}